import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "link"]
  static values = {
    scrollDownOnly: Boolean
  }

  connect() {
    this.ticking = false
    this.visible = false
    this.lastScrollY = window.scrollY
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleViewportChange = this.handleViewportChange.bind(this)
    this.handleFocusIn = this.handleFocusIn.bind(this)
    this.handleFocusOut = this.handleFocusOut.bind(this)

    this.hide()
    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleResize, { passive: true })
    window.addEventListener("focusin", this.handleFocusIn)
    window.addEventListener("focusout", this.handleFocusOut)
    window.visualViewport?.addEventListener("resize", this.handleViewportChange)
    window.visualViewport?.addEventListener("scroll", this.handleViewportChange)

    if (!this.scrollDownOnlyValue) this.updateVisibility()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
    window.removeEventListener("focusin", this.handleFocusIn)
    window.removeEventListener("focusout", this.handleFocusOut)
    window.visualViewport?.removeEventListener("resize", this.handleViewportChange)
    window.visualViewport?.removeEventListener("scroll", this.handleViewportChange)
    if (this.frameId) window.cancelAnimationFrame(this.frameId)
  }

  handleScroll() {
    if (this.ticking) return

    this.ticking = true
    this.frameId = window.requestAnimationFrame(() => {
      this.updateVisibility()
      this.ticking = false
      this.frameId = undefined
    })
  }

  updateVisibility() {
    const currentScrollY = window.scrollY

    if (this.shouldStayHidden() || currentScrollY < 80) {
      this.hide()
      this.lastScrollY = currentScrollY
      return
    }

    if (!this.scrollDownOnlyValue) {
      this.show()
      this.lastScrollY = currentScrollY
      return
    }

    const distance = currentScrollY - this.lastScrollY
    if (Math.abs(distance) < 6) return

    if (distance > 0) this.show()
    this.lastScrollY = currentScrollY
  }

  handleResize() {
    this.lastScrollY = window.scrollY
    this.updateVisibility()
  }

  handleViewportChange() {
    this.lastScrollY = window.scrollY
    this.updateVisibility()
  }

  handleFocusIn(event) {
    if (this.isTextEntryField(event.target)) this.hide()
  }

  handleFocusOut(event) {
    if (!this.isTextEntryField(event.target)) return

    window.requestAnimationFrame(() => this.updateVisibility())
  }

  shouldStayHidden() {
    return this.isTextEntryField(document.activeElement) || this.isSoftwareKeyboardOpen()
  }

  isTextEntryField(element) {
    if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) return true
    if (!(element instanceof HTMLInputElement)) return false

    return !["button", "checkbox", "file", "hidden", "image", "radio", "reset", "submit"].includes(element.type)
  }

  isSoftwareKeyboardOpen() {
    if (!window.visualViewport) return false

    const layoutViewportHeight = Math.max(window.innerHeight, document.documentElement.clientHeight)
    return layoutViewportHeight - window.visualViewport.height > 120
  }

  show() {
    if (this.visible || !this.hasContainerTarget) return

    this.visible = true
    this.containerTarget.classList.add("customer-floating-phone-cta--visible")
    this.containerTarget.setAttribute("aria-hidden", "false")
    if (this.hasLinkTarget) this.linkTarget.removeAttribute("tabindex")
  }

  hide() {
    if (this.hasContainerTarget) {
      this.containerTarget.classList.remove("customer-floating-phone-cta--visible")
      this.containerTarget.setAttribute("aria-hidden", "true")
    }

    if (this.hasLinkTarget) {
      this.linkTarget.setAttribute("tabindex", "-1")
    }

    this.visible = false
  }
}
